import { useContext } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { PAYMENT_METHOD, RECIPIENT_TYPES, TEMPLATE_CREATION_STEPS } from 'components/common/constants';
import { CYRILLIC_REGEXP } from 'components/common/PaymentForm/ProviderForms/ModelValidators/utils';
import i18nContext from 'components/i18n-context';
import CreateTemplateModel from 'components/Templates/models/CreateTemplateModel';
import { getTemplateStepErrorMessage } from 'components/Templates/utils/getTemplateStepErrorMessage';
import { CURRENCIES } from 'services/currenciesConstant';
import { amountFormattedValue } from 'services/utils';
import { ActionButton } from 'uikit/ActionButton/ActionButton';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import './ReviewAndSaveTemplateStep.scss';

const ReviewAndSaveTemplateStep = ({ paymentStore, customerNumber }) => {
  const i18n = useContext(i18nContext);

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      templateName: paymentStore.templateData?.templateName || ''
    },
    validationSchema: Yup.object().shape({
      templateName: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(255, i18n.getMessage('client.validation.message.maxSize', { size: 255 }))
        .test(
          'no-cyrillic',
          i18n.getMessage('client.validation.message.noCyrillic'),
          (value) => !CYRILLIC_REGEXP.test(value || '')
        )
    }),
    onSubmit: async (values) => {
      paymentStore.setTemplateData({ ...paymentStore.templateData, ...values });

      if (paymentStore.isTemplateUpdating) {
        const requestData = CreateTemplateModel({ ...paymentStore.templateData, ...values });
        await paymentStore.updateTemplate(customerNumber, requestData, paymentStore.editTemplateId);
      } else {
        const requestData = CreateTemplateModel({ ...paymentStore.templateData, ...values });
        await paymentStore.createTemplate(customerNumber, requestData);
      }
    }
  });

  const { values, errors, handleSubmit, handleChange } = form;
  const data = paymentStore.templateData;

  const getSectionError = (fields) => {
    if (!paymentStore.error) return null;

    if (Array.isArray(paymentStore.error.details)) {
      const found = paymentStore.error.details.find((item) =>
        fields.some((f) => item.field === f || item.field.split('.').pop() === f)
      );

      if (found) {
        const shortField = fields.find((f) => found.field === f || found.field.split('.').pop() === f) || found.field;

        return `${shortField}: ${getTemplateStepErrorMessage(i18n, paymentStore.error, shortField)}`;
      }
    }

    if (paymentStore.error.fields) {
      for (const field of fields) {
        if (paymentStore.error.fields[field]) {
          return getTemplateStepErrorMessage(i18n, paymentStore.error, field);
        }
      }
    }

    return null;
  };

  const hasSectionError = (fields) => {
    if (!paymentStore.error) return false;

    if (Array.isArray(paymentStore.error.details)) {
      return paymentStore.error.details.some((item) =>
        fields.some((f) => item.field === f || item.field.split('.').pop() === f)
      );
    }

    if (paymentStore.error.fields) {
      return fields.some((field) => paymentStore.error.fields[field]);
    }

    return false;
  };

  const renderDetailRow = (label, value, valueClassName) => {
    if (!value) return null;
    return (
      <div className={'template-detail-row'}>
        <div className={'template-detail-label'}>{label}</div>
        <div className={`template-detail-value ${valueClassName || ''}`}>{value}</div>
      </div>
    );
  };

  return (
    <div className={'template-confirmation-container'}>
      <div className={'template-confirmation-card'}>
        <div className={'template-section'}>
          <div className={'template-section-title-wrapper'}>
            <h3
              className={`template-section-title ${hasSectionError(['amount', 'transferDetails']) ? 'has-error' : ''}`}
            >
              {i18n.getMessage('templates.creationConfirmation.step.paymentDetails')}
            </h3>
            <ActionButton
              tooltipText={i18n.getMessage('templates.creationConfirmation.button.edit.tooltip')}
              iconType={'edit-for-approval'}
              onClick={() => paymentStore.setCurrentTemplateStep(TEMPLATE_CREATION_STEPS.PAYMENT_DETAILS)}
            />
          </div>
          {renderDetailRow(
            i18n.getMessage('templates.creationConfirmation.field.amount'),
            //eslint-disable-next-line max-len
            `${CURRENCIES[data.currency]?.symbol || data.currency} ${amountFormattedValue(data.amount, data.isCryptoPayment ? 8 : 2)}`,
            'amount-highlight'
          )}
          {!data.isCryptoPayment &&
            renderDetailRow(
              i18n.getMessage('templates.creationConfirmation.field.transferDetails'),
              data.transferDetails
            )}
          {getSectionError(['amount', 'transferDetails']) && (
            <div className={'template-confirmation-error-message'}>
              {getSectionError(['amount', 'transferDetails'])}
            </div>
          )}
        </div>

        <div className={'section-divider'} />

        <div className={'template-section'}>
          <div className={'template-section-title-wrapper'}>
            <h3
              className={`template-section-title ${
                hasSectionError(['accountNumber', 'sortCode', 'iban', 'network']) ? 'has-error' : ''
              }`}
            >
              {i18n.getMessage('templates.creationConfirmation.step.accountDetails')}
            </h3>
            <ActionButton
              tooltipText={i18n.getMessage('templates.creationConfirmation.button.edit.tooltip')}
              iconType={'edit-for-approval'}
              onClick={() => paymentStore.setCurrentTemplateStep(TEMPLATE_CREATION_STEPS.ACCOUNT_DETAILS)}
            />
          </div>
          {renderDetailRow(i18n.getMessage('templates.creationConfirmation.field.accountNumber'), data.accountNumber)}
          {renderDetailRow(i18n.getMessage('templates.creationConfirmation.field.sortCode'), data.sortCode)}
          {renderDetailRow(i18n.getMessage('templates.creationConfirmation.field.recipientIban'), data.iban)}
          {getSectionError(['accountNumber', 'sortCode', 'iban', 'network']) && (
            <div className={'template-confirmation-error-message'}>
              {getSectionError(['accountNumber', 'sortCode', 'iban', 'network'])}
            </div>
          )}
        </div>

        {!data.isCryptoPayment && <div className={'section-divider'} />}

        {paymentStore.templateSteps?.includes(TEMPLATE_CREATION_STEPS.BANK_DETAILS) && (
          <>
            <div className={'template-section'}>
              <div className={'template-section-title-wrapper'}>
                <h3
                  className={`template-section-title ${
                    hasSectionError(['bankName', 'bankCountry', 'bic']) ? 'has-error' : ''
                  }`}
                >
                  {i18n.getMessage('templates.creationConfirmation.step.bankDetails')}
                </h3>
                <ActionButton
                  tooltipText={i18n.getMessage('templates.creationConfirmation.button.edit.tooltip')}
                  iconType={'edit-for-approval'}
                  onClick={() => paymentStore.setCurrentTemplateStep(TEMPLATE_CREATION_STEPS.BANK_DETAILS)}
                />
              </div>
              {renderDetailRow(i18n.getMessage('templates.creationConfirmation.field.recipientBicSwift'), data.bic)}
              {renderDetailRow(i18n.getMessage('templates.creationConfirmation.field.bankName'), data.bankName)}
              {renderDetailRow(i18n.getMessage('templates.creationConfirmation.field.bankCountry'), data.bankCountry)}
              {getSectionError(['bankName', 'bankCountry', 'bic']) && (
                <div className={'template-confirmation-error-message'}>
                  {getSectionError(['bankName', 'bankCountry', 'bic'])}
                </div>
              )}
            </div>
            <div className={'section-divider'} />
          </>
        )}

        {paymentStore.templateSteps?.includes(TEMPLATE_CREATION_STEPS.RECIPIENT_DETAILS) && (
          <>
            <div className={'template-section'}>
              <div className={'template-section-title-wrapper'}>
                <h3
                  className={`template-section-title ${
                    hasSectionError(['recipientType', 'companyName', 'firstName', 'lastName']) ? 'has-error' : ''
                  }`}
                >
                  {i18n.getMessage('templates.creationConfirmation.step.recipientDetails')}
                </h3>
                <ActionButton
                  tooltipText={i18n.getMessage('templates.creationConfirmation.button.edit.tooltip')}
                  iconType={'edit-for-approval'}
                  onClick={() => paymentStore.setCurrentTemplateStep(TEMPLATE_CREATION_STEPS.RECIPIENT_DETAILS)}
                />
              </div>
              {renderDetailRow(
                i18n.getMessage('templates.creationConfirmation.field.recipientType'),
                data.recipientType
              )}
              {data.recipientType === RECIPIENT_TYPES.COMPANY &&
                renderDetailRow(
                  i18n.getMessage('templates.creationConfirmation.field.recipientCompanyName'),
                  data.companyName
                )}

              {data.recipientType === RECIPIENT_TYPES.INDIVIDUAL && (
                <>
                  {renderDetailRow(
                    i18n.getMessage('templates.creationConfirmation.field.recipientFirstName'),
                    data.firstName
                  )}
                  {renderDetailRow(
                    i18n.getMessage('templates.creationConfirmation.field.recipientLastName'),
                    data.lastName
                  )}
                </>
              )}
              {getSectionError(['recipientType', 'companyName', 'firstName', 'lastName']) && (
                <div className={'template-confirmation-error-message'}>
                  {getSectionError(['recipientType', 'companyName', 'firstName', 'lastName'])}
                </div>
              )}
            </div>

            {data.paymentMethod === PAYMENT_METHOD.SWIFT && <div className={'section-divider'} />}
          </>
        )}

        {paymentStore.templateSteps?.includes(TEMPLATE_CREATION_STEPS.RECIPIENT_ADDITIONAL_DETAILS) && (
          <>
            <div className={'template-section'}>
              <div className={'template-section-title-wrapper'}>
                <h3
                  className={`template-section-title ${
                    hasSectionError(['registrationNumber', 'country', 'city', 'postCode', 'address']) ? 'has-error' : ''
                  }`}
                >
                  {i18n.getMessage('templates.creationConfirmation.step.recipientAdditionalDetails')}
                </h3>
                <ActionButton
                  tooltipText={i18n.getMessage('templates.creationConfirmation.button.edit.tooltip')}
                  iconType={'edit-for-approval'}
                  onClick={() =>
                    paymentStore.setCurrentTemplateStep(TEMPLATE_CREATION_STEPS.RECIPIENT_ADDITIONAL_DETAILS)
                  }
                />
              </div>
              {renderDetailRow(
                i18n.getMessage('templates.creationConfirmation.field.registrationNumber'),
                data.registrationNumber
              )}
              {renderDetailRow(i18n.getMessage('templates.creationConfirmation.field.recipientCity'), data.city)}
              {renderDetailRow(i18n.getMessage('templates.creationConfirmation.field.recipientCountry'), data.country)}
              {renderDetailRow(
                i18n.getMessage('templates.creationConfirmation.field.recipientPostalCode'),
                data.postCode
              )}
              {renderDetailRow(i18n.getMessage('templates.creationConfirmation.field.recipientAddress'), data.address)}
              {getSectionError(['registrationNumber', 'country', 'city', 'postCode', 'address']) && (
                <div className={'template-confirmation-error-message'}>
                  {getSectionError(['registrationNumber', 'country', 'city', 'postCode', 'address'])}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <form onSubmit={handleSubmit} className={'template-confirmation-form'}>
        <Input
          className={'template-confirmation-input'}
          name={'templateName'}
          label={i18n.getMessage('templates.form.templateName.label')}
          placeholder={i18n.getMessage('templates.form.templateName.placeholder')}
          value={values.templateName}
          error={errors.templateName}
          onChange={handleChange}
          isRequired
        />
        <Button
          className={'template-confirmation-create-button'}
          type={'primary'}
          roleType={'submit'}
          isDisabled={paymentStore.isLoading}
        >
          {i18n.getMessage('templates.creationConfirmation.button.save')}
        </Button>
      </form>
    </div>
  );
};

ReviewAndSaveTemplateStep.propTypes = {
  customerNumber: PropTypes.string,
  paymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  paymentStore: stores.paymentStore
}))(observer(ReviewAndSaveTemplateStep));
